import { ActionButton } from '/src/components/actionButton';
import userApi from '@api/userApi';
import ItemEditor from '@components/itemEditor';
import OrgRolesSelect from '@components/orgRolesSelect';
import useAuth from '@hooks/useAuth';
import { t } from '@i18n';
import { Alert, Group, TextInput } from '@mantine/core';
import type { PublicUser } from '@models/user';
import { IconInfoCircle } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { createForm } from 'cross-state/react';
import { pick } from 'radashi';

export const Route = createFileRoute('/profile/users/$id')({
  component: UserEditor,
});

const userForm = createForm<PublicUser>({
  defaultValue: {
    id: '',
    userName: '',
    firstName: '',
    lastName: '',
    orgRoles: [],
  },
  validations: {
    userName: {
      required: (x, { original }) => (original ? true : !!x),
      email: (x) => !!x?.includes('@'),
    },
    firstName: { required: (x) => !!x },
    lastName: { required: (x) => !!x },
    orgRoles: { atLeastOneOrg: (x) => !!x?.length },
  },
});

function UserEditor() {
  const { hasClaim } = useAuth();
  const { id } = Route.useParams();
  const existing: PublicUser | undefined = userApi.get(id).useCache({ disabled: id === 'new' })[0];

  return (
    <ItemEditor
      domain="users"
      api={userApi}
      readonly={(user) =>
        !user
          ? !hasClaim('users/create', 'any')
          : !hasClaim('users/write', user.orgRoles.length === 0 ? 'any' : user.orgRoles.map((x) => x.orgId))
      }
      deletable={
        !!existing &&
        hasClaim('users/delete', existing.orgRoles.length === 0 ? 'any' : existing.orgRoles.map((x) => x.orgId))
      }
      form={userForm}
      name={(user) => user.userName ?? ''}
      prepareDraft={(user) => {
        return pick(
          user,
          user.id
            ? ['id', 'v', 'firstName', 'lastName', 'orgRoles']
            : ['id', 'userName', 'firstName', 'lastName', 'orgRoles'],
        );
      }}
    >
      {({ isNew, item }) => (
        <>
          <userForm.Field
            name="userName"
            render={(props) => (
              <TextInput
                {...props}
                formNoValidate
                label={t('users.properties.email.label')}
                description={t('users.properties.email.description')}
                withAsterisk
                autoComplete="email"
                spellCheck={false}
                disabled={!isNew}
              />
            )}
          />

          <userForm.Field
            name="firstName"
            render={(props) => (
              <TextInput
                {...props}
                value={props.value ?? ''}
                label={t('users.properties.firstName.label')}
                withAsterisk
                autoComplete="off"
                spellCheck={false}
              />
            )}
          />

          <userForm.Field
            name="lastName"
            render={(props) => (
              <TextInput
                {...props}
                value={props.value ?? ''}
                label={t('users.properties.lastName.label')}
                withAsterisk
                autoComplete="off"
                spellCheck={false}
              />
            )}
          />

          <userForm.Field
            name="orgRoles"
            render={(props) => (
              <OrgRolesSelect
                {...props}
                value={props.value ?? []}
                required
                assignClaim={isNew ? 'users/create' : 'users/write'}
              />
            )}
          />

          <div css={{ flex: 1000 }} />

          {isNew && (
            <Alert color="blue" icon={<IconInfoCircle />}>
              {t('users.newUserHint')}
            </Alert>
          )}

          {!isNew && item?.email && !item.email.isConfirmed && <UnconfirmedUserDetails user={item} />}
        </>
      )}
    </ItemEditor>
  );
}

function UnconfirmedUserDetails({ user }: { user: PublicUser }) {
  const hasExpired = user.email?.verificationTokenExpiresOn
    ? new Date(user.email.verificationTokenExpiresOn) < new Date()
    : false;

  const { hasClaim } = useAuth();
  const canResendEmail = hasClaim(
    'users/write',
    user.orgRoles.map((x) => x.orgId),
  );

  async function resendEmail() {
    await userApi.resendEmail(user.email?.address ?? '');
  }

  return (
    <Alert icon={<IconInfoCircle />} color={hasExpired ? 'yellow' : 'blue'} title={t('users.unconfirmed.title')}>
      {t('users.unconfirmed.description')}

      <br />

      {user.email?.verificationTokenExpiresOn && (
        <>
          <span>{hasExpired ? t('users.unconfirmed.hasExpired') : t('users.unconfirmed.willExpire')}: </span>
          <span>{t.dateTimeFormat(user.email.verificationTokenExpiresOn)}</span>

          {canResendEmail && (
            <Group mt="md">
              <ActionButton onClick={resendEmail}>{t('users.unconfirmed.resend')}</ActionButton>
            </Group>
          )}
        </>
      )}
    </Alert>
  );
}
